import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MessageSquare, Lock, Flame } from 'lucide-react';
import BanterPost from './BanterPost';
import BanterComposer from './BanterComposer';
import QuickBanterSheet from './QuickBanterSheet';
import { buildFeed } from './banterFeed';
import { boardSource } from './banterSource';
import * as banterApi from '../../api/banter';

/**
 * One Banterboard, asked for either by league tournament or by game.
 *
 * <p>Both can lead to the same board, and usually do. A league's game is handed the
 * league's board rather than one of its own — so the conversation appears on the game
 * and on the league instead of being split across two quiet threads, and it survives
 * auto-recreation replacing that game every few weeks. Only a standalone game has a
 * board of its own, and that one closes when the game ends.
 *
 * Which of the two routes to use lives in `banterSource`, not here.
 *
 * <p>Who and what can be named comes from the board too, rather than from whatever the
 * page around it happens to hold. That was a prop, and it was wrong in two ways at once:
 * the League page passed its member list and the Game page passed a raw roster of a
 * different shape, so the player picker rendered nothing at all on a Game; and the teams
 * came from `/api/teams`, which is every team on the platform rather than the ones in the
 * competition being talked about.
 *
 * Three ways this can come back refused, and they mean different things to whoever
 * is reading:
 *   401 — signed out. The app handles that globally.
 *   403 — the account is closed. Not an age message any more: the platform is 18+, so
 *         everybody holding an account is old enough and telling them to check their
 *         date of birth would send them looking at the wrong thing.
 *   404 — not a member of this league, or not on this game's roster. Deliberately
 *         indistinguishable from a board that does not exist.
 */
function Banterboard({ tournamentId, gameId, house = false, tournamentName, currentUserId, showHeading = false, onNavigate }) {
  const [posts, setPosts] = useState([]);
  const [themes, setThemes] = useState([]);
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refused, setRefused] = useState(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const [composing, setComposing] = useState(false);
  const [open, setOpen] = useState(true);

  const source = useMemo(() => boardSource({ tournamentId, gameId, house }), [tournamentId, gameId, house]);

  const load = useCallback(async () => {
    try {
      const data = await source.load();
      setPosts(data.posts);
      setOpen(data.open);
      setRefused(null);

      // Only worth fetching once the board is known to be readable — a refused board
      // needs neither a vocabulary nor a target list. Both are best-effort: losing the
      // targets should cost you the phrases that need one, not the whole board.
      //
      // A board nobody may post to skips both entirely. There is nothing to say and
      // nobody to name, so two requests that could only ever furnish a composer that
      // will not render are two requests not made.
      if (!source.postable) {
        return;
      }
      const [themeList, boardTargets] = await Promise.all([
        banterApi.getThemes().catch(() => []),
        source.targets().catch(() => ({ players: [], teams: [] })),
      ]);
      setThemes(themeList);
      setMembers(boardTargets.players ?? []);
      setTeams(boardTargets.teams ?? []);
    } catch (err) {
      if (err.name === 'ApiError' && (err.status === 403 || err.status === 404)) {
        setRefused(err.status);
      } else {
        setError('Could not load the board');
      }
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => {
    load();
  }, [load]);

  const post = async (choice) => {
    setError('');
    setPosting(true);
    try {
      const created = await source.post(choice);
      // Prepend rather than refetch — the board is newest-first, and a round trip
      // here makes posting feel slower than it is.
      setPosts((current) => [created, ...current]);
      return true;
    } catch (err) {
      if (err.name === 'ApiError') {
        setError(err.body?.error || 'Could not post that');
      } else {
        setError('Could not post that');
      }
      return false;
    } finally {
      setPosting(false);
    }
  };

  const feed = useMemo(() => buildFeed(posts), [posts]);

  if (loading) return null;

  // A closed account. Say why rather than showing an empty room.
  if (refused === 403) {
    return (
      <div className="board-locked">
        <Lock size={20} />
        <p>This account is closed.</p>
      </div>
    );
  }

  // Not a member — or no such board. Same answer either way, by design.
  if (refused === 404) return null;

  return (
    <div className="league-board" aria-label={`${tournamentName || 'League'} Banterboard`}>
      {/* Only when there are several boards to tell apart. On its own tab with one
          tournament the tab is the heading, and a second one is just noise. */}
      {showHeading && (
        <h3 className="board-heading">
          <Flame size={16} />
          {tournamentName}
        </h3>
      )}

      {/* A finished standalone game's board is kept to read, not to write on — so the
          composer goes rather than sitting there to reject whatever gets typed into it.
          A league board is never closed.

          A board nobody may post to shows neither, and the difference matters: a closed
          game board says "that's full time" because it was a conversation that ended,
          and the House board never was one. */}
      {source.postable && (open ? (
        <BanterComposer onOpen={() => setComposing(true)} disabled={themes.length === 0} />
      ) : (
        <div className="board-closed">
          <Lock size={16} />
          <span>That's full time. The board's closed, but the banter stays.</span>
        </div>
      ))}

      {error && <div className="error-message">{error}</div>}

      {posts.length === 0 ? (
        <div className="board-empty">
          <MessageSquare size={40} />
          {/* A board somebody could post to is waiting for one of them; a board only
              the house writes to is waiting for the house, and telling a player to go
              first there is an invitation they cannot accept. */}
          <p>{source.postable
            ? 'Nothing yet. Someone has to go first.'
            : 'Nothing from the house yet.'}</p>
        </div>
      ) : (
        <div className="board-feed">
          {feed.map((row) => {
            if (row.kind === 'day') {
              return (
                <div key={row.key} className="board-day" role="separator">
                  <span>{row.label}</span>
                </div>
              );
            }
            if (row.kind === 'round') {
              return (
                <div key={row.key} className="board-round" role="separator">
                  <span>Round {row.round}</span>
                </div>
              );
            }
            // Passed straight through. The board has no opinion about where a post's
            // link should land — only the page around it knows what "progress" means, and
            // on the League page it means nothing, so no button is drawn there.
            return <BanterPost key={row.key} post={row.post} onNavigate={onNavigate} />;
          })}
        </div>
      )}

      {composing && (
        <QuickBanterSheet
          themes={themes}
          members={members}
          teams={teams}
          currentUserId={currentUserId}
          onPost={post}
          posting={posting}
          onClose={() => setComposing(false)}
        />
      )}
    </div>
  );
}

export default Banterboard;
